import { ComponentProps, useMemo, useState } from "react";
import {
  Badge,
  Button,
  ButtonGroup,
  cn,
  DestructiveConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useCurrentTimestamp,
  useStateWithSetAction,
} from "@tellescope/ui";
import { addDays } from "date-fns/addDays";
import { addMonths } from "date-fns/addMonths";
import { addYears } from "date-fns/addYears";
import { isWithinInterval } from "date-fns/isWithinInterval";
import { formatInTimeZone } from "date-fns-tz";
import { useForm } from "@tellescope/forms";
import { TodayButton } from "./TodayButton";
import { useTranslation } from "react-i18next";
import { usePermissionQuery } from "@tellescope/tanstack-query";
import { getDateFnsLocale } from "@tellescope/utils";
import { CalendarView, CommonCalendarOptions, DateRange } from "../../types";
import { CalendarFilters, FAKE_CALENDAR_USER_ID } from "../../filters";
import { useCalendarEventsWithFilters } from "../../hooks";
import { getRangeFromDateAndView } from "../../utils";
import { PaginationControls } from "./PaginationControls";
import { CalendarPlus, CalendarRange, Columns, Filter, Grid2x2, Grid3X3, List, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { set } from "date-fns/set";
import { EventCreateDialog } from "../dialogs";
import { LOOKBACK_IN_MS_BY_VIEW } from "../constants";
import { AvailabilityHeaderRow } from "../AvailabilityHeaderRow";
import { differenceInDays } from "date-fns/differenceInDays";

export type GetNavigationPathFn = (data: { newViewType: CalendarView } & DateRange) => string;

interface NavigationLinks extends Record<CalendarView, string> {
  previousPage: string;
  nextPage: string;
  today: string;
}

interface ProgramaticNavigationOptions {
  onViewTypeClick: (data: DateRange & { viewType: CalendarView }) => void;
  onNextPageClick: (range: DateRange) => void;
  onPreviousPageClick: (range: DateRange) => void;
  onTodayClick: (range: DateRange) => void;
}

export interface CalendarHeaderViewOption {
  id: string;
  title: string;
}

const ALL_VIEW_SENTINEL = "__all__";

export interface CalendarHeaderProps extends DateRange, CommonCalendarOptions {
  selectedViewType: CalendarView;
  navigationOptions: GetNavigationPathFn | ProgramaticNavigationOptions;
  referenceDate: Date;
  className?: string;
  view: string;
  filters?: CalendarFilters;
  currentUserId?: string;
  savedViews?: CalendarHeaderViewOption[];
  selectedSavedViewId?: string;
  onSelectSavedView?: (id: string | undefined) => void;
  hasUnsavedFilterChanges?: boolean;
  canCreateSavedView?: boolean;
  canDeleteSavedView?: boolean;
  onCreateSavedView?: (title: string) => void;
  onDeleteSavedView?: () => void;
  onOpenFilterDialog?: () => void;
  availabilityUserId?: string;
  getAvailabilityRangeClick: (startDate: Date) => (() => void) | { to: string; replace?: boolean };
}

export const CalendarHeader = ({
  startDate,
  endDate,
  selectedViewType,
  navigationOptions,
  referenceDate,
  timezone,
  weekStart,
  className,
  view,
  filters,
  currentUserId,
  savedViews,
  selectedSavedViewId,
  onSelectSavedView,
  hasUnsavedFilterChanges,
  canCreateSavedView,
  canDeleteSavedView,
  onCreateSavedView,
  onDeleteSavedView,
  onOpenFilterDialog,
  availabilityUserId,
  getAvailabilityRangeClick,
}: CalendarHeaderProps) => {
  const { t, i18n } = useTranslation("calendar");
  const shouldUseLinks = typeof navigationOptions === "function";
  const resolvedTimezone = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const selectedUserIds = useMemo(
    () => (filters?.userIds ?? []).filter((id) => id !== FAKE_CALENDAR_USER_ID),
    [filters?.userIds]
  );
  const eventCountQuery = useCalendarEventsWithFilters({
    referenceDate,
    view: selectedViewType,
    timezone,
    weekStart,
    count: true,
    lookbackInMS: LOOKBACK_IN_MS_BY_VIEW[view],
    filters,
    currentUserId,
  });
  const eventCountText = useMemo(() => {
    if (eventCountQuery.isLoading || eventCountQuery.hasNextPage || eventCountQuery.data === undefined) {
      return t("Loading...");
    }

    return t("{{count}} events", { count: eventCountQuery.data });
  }, [eventCountQuery.data, eventCountQuery.hasNextPage, eventCountQuery.isLoading, t]);
  const [isCreating, setIsCreating] = useState(false);

  const [isDeleteViewOpen, setIsDeleteViewOpen] = useState(false);

  const saveAsViewForm = useForm({
    fields: {
      title: { type: "text", required: true, initialValue: "" },
    },
    validateOn: "blur",
    onSubmit: (values) => {
      if (!values.title || !onCreateSavedView) return;
      onCreateSavedView(values.title);
      setIsSaveAsViewOpen(false);
    },
  });
  const [isSaveAsViewOpen, setIsSaveAsViewOpen] = useStateWithSetAction(false, (isOpen) => {
    if (!isOpen) {
      saveAsViewForm.resetForm();
    }
  });

  const {
    create: { access: canCreate },
  } = usePermissionQuery({
    model: "calendar_events",
    actions: ["create"],
  });

  const { nextPageRange, previousPageRange } = useMemo(() => {
    const nextPageRange: DateRange = {
      startDate,
      endDate,
    };
    const previousPageRange: DateRange = {
      startDate,
      endDate,
    };
    switch (selectedViewType) {
      case "list":
      case "month":
        nextPageRange.startDate = addMonths(nextPageRange.startDate, 1);
        nextPageRange.endDate = addMonths(nextPageRange.endDate, 1);
        previousPageRange.startDate = addMonths(previousPageRange.startDate, -1);
        previousPageRange.endDate = addMonths(previousPageRange.endDate, -1);
        break;
      case "year":
        nextPageRange.startDate = addYears(nextPageRange.startDate, 1);
        nextPageRange.endDate = addYears(nextPageRange.endDate, 1);
        previousPageRange.startDate = addYears(previousPageRange.startDate, -1);
        previousPageRange.endDate = addYears(previousPageRange.endDate, -1);
        break;
      case "week":
        nextPageRange.startDate = addDays(nextPageRange.startDate, 7);
        nextPageRange.endDate = addDays(nextPageRange.endDate, 7);
        previousPageRange.startDate = addDays(previousPageRange.startDate, -7);
        previousPageRange.endDate = addDays(previousPageRange.endDate, -7);
        break;
      case "day":
        nextPageRange.startDate = addDays(nextPageRange.startDate, 1);
        nextPageRange.endDate = addDays(nextPageRange.endDate, 1);
        previousPageRange.startDate = addDays(previousPageRange.startDate, -1);
        previousPageRange.endDate = addDays(previousPageRange.endDate, -1);
        break;
    }
    return {
      nextPageRange,
      previousPageRange,
    };
  }, [endDate, selectedViewType, startDate]);

  const links = useMemo<NavigationLinks | undefined>(() => {
    if (!shouldUseLinks) return undefined;

    const paginationSlice: Pick<NavigationLinks, "nextPage" | "previousPage"> = {
      nextPage: navigationOptions({ newViewType: selectedViewType, ...nextPageRange }),
      previousPage: navigationOptions({ newViewType: selectedViewType, ...previousPageRange }),
    };

    const todayIsInCurrentRange = isWithinInterval(new Date(), { start: startDate, end: endDate });
    const dateForCalculation = todayIsInCurrentRange ? new Date() : startDate;

    return {
      ...paginationSlice,
      today: navigationOptions({
        newViewType: "day",
        ...getRangeFromDateAndView(new Date(), "day"),
      }),
      year: navigationOptions({
        newViewType: "year",
        ...getRangeFromDateAndView(dateForCalculation, "year"),
      }),
      month: navigationOptions({
        newViewType: "month",
        ...getRangeFromDateAndView(dateForCalculation, "month"),
      }),
      list: navigationOptions({
        newViewType: "list",
        ...getRangeFromDateAndView(dateForCalculation, "month"),
      }),
      week: navigationOptions({
        newViewType: "week",
        ...getRangeFromDateAndView(dateForCalculation, "week"),
      }),
      day: navigationOptions({
        newViewType: "day",
        ...getRangeFromDateAndView(dateForCalculation, "day"),
      }),
    };
  }, [endDate, navigationOptions, nextPageRange, previousPageRange, selectedViewType, shouldUseLinks, startDate]);

  const todayButtonProps = useMemo<ComponentProps<typeof TodayButton> | undefined>(() => {
    if (links) {
      return {
        route: links.today,
        replace: true,
      };
    }

    if (typeof navigationOptions === "object") {
      return {
        onClick: () => navigationOptions.onTodayClick(getRangeFromDateAndView(new Date(), "day")),
      };
    }
  }, [links, navigationOptions]);
  const paginationControlsProps = useMemo<ComponentProps<typeof PaginationControls> | undefined>(() => {
    if (links) {
      return {
        startDate,
        endDate,
        onNextPageClick: links.nextPage,
        onPreviousPageClick: links.previousPage,
        replace: true,
        timezone,
      };
    }

    if (typeof navigationOptions === "object") {
      return {
        startDate,
        endDate,
        onNextPageClick: () => navigationOptions.onNextPageClick(nextPageRange),
        onPreviousPageClick: () => navigationOptions.onPreviousPageClick(previousPageRange),
        timezone,
      };
    }
  }, [endDate, links, navigationOptions, nextPageRange, previousPageRange, startDate, timezone]);

  const showFilterControls = onOpenFilterDialog !== undefined && savedViews !== undefined;
  const selectValue = selectedSavedViewId ?? ALL_VIEW_SENTINEL;
  const showDeleteSegment = showFilterControls && !!selectedSavedViewId && !!canDeleteSavedView;
  const currentTimestamp = useCurrentTimestamp({ pollingRateInMs: 1000 * 60, snapTo: "minute" });

  const availabilityFutureDayCount = useMemo(() => {
    switch (selectedViewType) {
      case "day":
        return 0;
      default:
        return Math.min(differenceInDays(endDate, currentTimestamp), 7);
    }
  }, [currentTimestamp, endDate, selectedViewType]);

  return (
    <div className="@container">
      <section
        className={cn(
          "flex flex-col gap-4 border-b py-4 px-4 @xl:flex-row @xl:items-center @xl:justify-between",
          className
        )}
      >
        <div className="flex items-center gap-3">
          {todayButtonProps ? <TodayButton {...todayButtonProps} /> : null}
          <div className="space-y-0.5">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold">
                {formatInTimeZone(
                  selectedViewType === "year" ? set(startDate, { month: new Date().getMonth() }) : startDate,
                  resolvedTimezone,
                  "MMMM yyyy",
                  { locale: getDateFnsLocale(i18n.language) }
                )}
              </h3>
              <Badge variant="secondary" className="text-primary">
                {eventCountText}
              </Badge>
            </div>
            {!!paginationControlsProps && <PaginationControls {...paginationControlsProps} />}
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <ButtonGroup>
            <Button
              data-testid="calendar-view-type-day"
              variant={selectedViewType === "day" ? "default" : "outline"}
              asChild={shouldUseLinks}
              hoverLabel={t("Day")}
              size="sm-icon"
            >
              {shouldUseLinks ? (
                <Link to={links?.day ?? ""} replace>
                  <List strokeWidth={1.8} />
                </Link>
              ) : (
                <List strokeWidth={1.8} />
              )}
            </Button>
            <Button
              data-testid="calendar-view-type-week"
              variant={selectedViewType === "week" ? "default" : "outline"}
              asChild={shouldUseLinks}
              hoverLabel={t("Week")}
              size="sm-icon"
            >
              {shouldUseLinks ? (
                <Link to={links?.week ?? ""} replace>
                  <Columns strokeWidth={1.8} />
                </Link>
              ) : (
                <Columns strokeWidth={1.8} />
              )}
            </Button>
            <Button
              data-testid="calendar-view-type-month"
              variant={selectedViewType === "month" ? "default" : "outline"}
              asChild={shouldUseLinks}
              hoverLabel={t("Month")}
              size="sm-icon"
            >
              {shouldUseLinks ? (
                <Link to={links?.month ?? ""} replace>
                  <Grid2x2 strokeWidth={1.8} />
                </Link>
              ) : (
                <Grid2x2 strokeWidth={1.8} />
              )}
            </Button>
            <Button
              data-testid="calendar-view-type-year"
              variant={selectedViewType === "year" ? "default" : "outline"}
              asChild={shouldUseLinks}
              hoverLabel={t("Year")}
              size="sm-icon"
            >
              {shouldUseLinks ? (
                <Link to={links?.year ?? ""} replace>
                  <Grid3X3 strokeWidth={1.8} />
                </Link>
              ) : (
                <Grid3X3 strokeWidth={1.8} />
              )}
            </Button>
            <Button
              data-testid="calendar-view-type-list"
              variant={selectedViewType === "list" ? "default" : "outline"}
              asChild={shouldUseLinks}
              size="sm-icon"
              hoverLabel={t("List")}
            >
              {shouldUseLinks ? (
                <Link to={links?.list ?? ""} replace>
                  <CalendarRange strokeWidth={1.8} />
                </Link>
              ) : (
                <CalendarRange strokeWidth={1.8} />
              )}
            </Button>
          </ButtonGroup>
          {canCreate && (
            <Button
              data-testid="calendar-create-event-button"
              variant="outline"
              size="sm-icon"
              onClick={() => setIsCreating(true)}
              hoverLabel={t("Create Event")}
            >
              <CalendarPlus />
            </Button>
          )}
          {showFilterControls && (
            <>
              <ButtonGroup>
                <Select
                  value={selectValue}
                  onValueChange={(value) => {
                    onSelectSavedView?.(value === ALL_VIEW_SENTINEL ? undefined : value);
                  }}
                >
                  <SelectTrigger data-testid="calendar-saved-views-trigger" className="w-auto max-w-[100px] px-1">
                    <SelectValue placeholder={t("All")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem data-testid="calendar-saved-view-item-all" value={ALL_VIEW_SENTINEL}>
                      {t("All")}
                    </SelectItem>
                    {savedViews?.map((view) => (
                      <SelectItem data-testid={`calendar-saved-view-item-${view.id}`} key={view.id} value={view.id}>
                        {view.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  data-testid="calendar-open-filter-dialog"
                  variant="outline"
                  className="relative size-8 p-0 border-l-0"
                  onClick={onOpenFilterDialog}
                  hoverLabel={t("Configure filters")}
                >
                  <Filter strokeWidth={1.8} />
                  {hasUnsavedFilterChanges && (
                    <span
                      data-testid="calendar-filters-unsaved-indicator"
                      className="absolute top-1 right-1 h-2 w-2 rounded-full bg-primary"
                    />
                  )}
                </Button>
                {showDeleteSegment && (
                  <Button
                    data-testid="calendar-delete-saved-view-button"
                    variant="outline"
                    className="size-8 p-0 border-l-0"
                    onClick={() => setIsDeleteViewOpen(true)}
                    hoverLabel={t("Delete View")}
                  >
                    <Trash2 strokeWidth={1.8} className="text-destructive" />
                  </Button>
                )}
              </ButtonGroup>
              {canCreateSavedView && hasUnsavedFilterChanges && (
                <Button
                  data-testid="calendar-save-as-view-button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsSaveAsViewOpen(true)}
                  hoverLabel={t("Save as View")}
                  className="font-light text-xs"
                >
                  {t("Save as View")}
                </Button>
              )}
            </>
          )}
        </div>
      </section>
      {!!availabilityUserId && (
        <AvailabilityHeaderRow
          userId={availabilityUserId}
          className="px-6 pt-2 border-b"
          getAvailabilityRangeClick={getAvailabilityRangeClick}
          futureDayCount={availabilityFutureDayCount}
        />
      )}
      <EventCreateDialog
        open={isCreating}
        onOpenChange={setIsCreating}
        onSuccess={() => setIsCreating(false)}
        initialFormValues={selectedUserIds.length ? { userIds: selectedUserIds } : undefined}
      />
      <Dialog open={isSaveAsViewOpen} onOpenChange={setIsSaveAsViewOpen}>
        <DialogContent className="w-[400px] overflow-hidden" divided>
          <div className="space-y-4 px-6 pt-4 pb-2">
            <DialogTitle className="font-medium text-md">{t("Save Current View")}</DialogTitle>
            <DialogDescription className="text-xs">
              {t("Enter a name for your new view. The current filters will be saved with it.")}
            </DialogDescription>
          </div>
          <form {...saveAsViewForm.getFormProps()}>
            <div className="flex flex-col gap-2 px-6 py-4">
              <Label>{t("View Name")}</Label>
              <Input
                data-testid="calendar-save-as-view-title-input"
                placeholder={t("e.g., High Priority Patients")}
                {...saveAsViewForm.getFieldProps("title")}
                onKeyDownCapture={(e) => {
                  if (e.key === "Enter" && saveAsViewForm.values.title) {
                    e.stopPropagation();
                    saveAsViewForm.submitForm();
                  }
                }}
                autoFocus
              />
            </div>
            <DialogFooter className="bg-muted">
              <Button variant="outline" onClick={() => setIsSaveAsViewOpen(false)}>
                {t("Cancel")}
              </Button>
              <Button data-testid="calendar-save-as-view-submit" type="submit" disabled={!saveAsViewForm.isValid}>
                {t("Save View")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <DestructiveConfirmDialog
        open={isDeleteViewOpen}
        onOpenChange={setIsDeleteViewOpen}
        onConfirm={() => {
          onDeleteSavedView?.();
        }}
        description={t("This will permanently delete the view. This action cannot be undone.")}
        confirmButtonTestId="calendar-delete-saved-view-confirm"
      />
    </div>
  );
};
